/**
 * Contact geometry: how two straight sides face each other, and how close
 * they must be to touch. The built-in rule, the wall coverage and the fixture
 * descriptions all measure contact this way.
 */
import type { Point } from '../schemas';

/** How close two surfaces must be to count as touching: an inch, for scan noise. */
export const CONTACT_TOLERANCE = 1 / 12;

/** Sides this close to parallel (the sine of the angle between them) can touch. */
const PARALLEL_SIN = Math.sin((3 * Math.PI) / 180);

const length = (a: Point, b: Point) => Math.hypot(b.x - a.x, b.y - a.y);

/**
 * How two line segments face each other: the distance between them, measured
 * across; how much of their length overlaps, measured along; and the `span`
 * of `a` that overlap covers, measured from `a`'s start. Null unless they are
 * parallel.
 */
export function facing(a: { a: Point; b: Point }, b: { a: Point; b: Point }): { distance: number; overlap: number; span: readonly [number, number] } | null {
  const lengthA = length(a.a, a.b);
  if (lengthA === 0 || length(b.a, b.b) === 0) return null;
  const dir = { x: (a.b.x - a.a.x) / lengthA, y: (a.b.y - a.a.y) / lengthA };
  const along = (p: Point) => (p.x - a.a.x) * dir.x + (p.y - a.a.y) * dir.y;
  const across = (p: Point) => (p.x - a.a.x) * -dir.y + (p.y - a.a.y) * dir.x;
  const acrossA = across(b.a), acrossB = across(b.b);
  const lengthB = length(b.a, b.b);
  if (Math.abs(acrossB - acrossA) / lengthB > PARALLEL_SIN) return null;
  const [from, to] = [along(b.a), along(b.b)].sort((x, y) => x - y);
  const span = [Math.max(0, from), Math.min(lengthA, to)] as const;
  return { distance: Math.abs((acrossA + acrossB) / 2), overlap: Math.max(0, span[1] - span[0]), span };
}
